import { useMemo } from 'react';
import { ALL_BANDS, bandColor } from '../lib/bands.js';
import { ALL_MODE_BUCKETS } from '../lib/modes.js';

const ALL_CONTINENTS = ['AF', 'AS', 'EU', 'NA', 'OC', 'SA', 'AN'];

export default function Toolbar({ settings, onUpdate }) {
  // Chip list is filtered through settings.visibleBands so the user can
  // hide rare bands from the toolbar without affecting the underlying
  // filter logic. Order is preserved from ALL_BANDS (wavelength-desc).
  const visibleBands = useMemo(() => {
    const v = new Set(settings.visibleBands || []);
    return ALL_BANDS.filter((b) => v.has(b));
  }, [settings.visibleBands]);

  const anyActive =
    settings.bands.length > 0 ||
    settings.modes.length > 0 ||
    settings.dxContinents.length > 0 ||
    settings.spotterContinents.length > 0 ||
    (settings.dxCallFilter || '').length > 0;
  const clearAll = () => onUpdate({
    bands: [], modes: [], dxContinents: [], spotterContinents: [],
    dxCallFilter: '',
  });

  // Layout: vertical stack on mobile (each ChipGroup is its own row,
  // scrolls horizontally on its own); single-line nowrap on ≥sm so the
  // desktop view is unchanged. Separators only render in the desktop row.
  return (
    <div className="px-3 py-1.5 bg-slate-900/60 border-b border-slate-800 text-xs
                    flex flex-col gap-1
                    sm:flex-row sm:items-center sm:gap-2 sm:flex-nowrap sm:overflow-x-auto sm:whitespace-nowrap">
      <DxCallInput
        value={settings.dxCallFilter || ''}
        onChange={(v) => onUpdate({ dxCallFilter: v })}
      />
      <Sep />
      <ChipGroup
        label="B"
        options={visibleBands}
        selected={settings.bands}
        onChange={(v) => onUpdate({ bands: v })}
        colored
      />
      <Sep />
      <ChipGroup
        label="M"
        options={ALL_MODE_BUCKETS}
        selected={settings.modes}
        onChange={(v) => onUpdate({ modes: v })}
      />
      <Sep />
      {/* dx = continent of the DX station; de = continent of the spotter
          (matches the "de <call>" prosign already used in the info card). */}
      <ChipGroup
        label="dx"
        options={ALL_CONTINENTS}
        selected={settings.dxContinents}
        onChange={(v) => onUpdate({ dxContinents: v })}
      />
      <Sep />
      <ChipGroup
        label="de"
        options={ALL_CONTINENTS}
        selected={settings.spotterContinents}
        onChange={(v) => onUpdate({ spotterContinents: v })}
      />
      {anyActive && (
        <button onClick={clearAll}
                className="self-start sm:self-auto sm:ml-1 text-slate-500 hover:text-slate-300 px-1">
          clear all
        </button>
      )}
    </div>
  );
}

function ChipGroup({ label, options, selected, onChange, colored }) {
  const sel = new Set(selected || []);
  const toggle = (opt) => {
    const next = new Set(sel);
    next.has(opt) ? next.delete(opt) : next.add(opt);
    onChange([...next]);
  };
  // On mobile each group gets its own row with its own horizontal scroll
  // — swiping inside Bands no longer drags Modes past you.
  return (
    <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap sm:overflow-visible">
      <span className="text-slate-500 font-mono shrink-0">{label}</span>
      {options.map((opt) => {
        const isSel = sel.has(opt);
        const baseStyle = colored && isSel
          ? { background: bandColor(opt), borderColor: bandColor(opt) }
          : undefined;
        return (
          <button
            key={opt}
            onClick={() => toggle(opt)}
            style={baseStyle}
            className={`shrink-0 px-2 py-0.5 rounded border font-mono ${
              isSel
                ? (colored
                    ? 'text-slate-900 font-semibold'
                    : 'bg-cyan-500 border-cyan-400 text-slate-900 font-semibold')
                : 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-300'
            }`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Sep() {
  // Separator only renders inline on ≥sm; on mobile the row break is
  // already a visual separator.
  return <span className="hidden sm:inline text-slate-700 select-none">|</span>;
}

function DxCallInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-500 font-mono shrink-0">DX</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder="call / partial"
        className="bg-slate-800 border border-slate-700 rounded px-2 py-0.5 font-mono
                   text-slate-200 placeholder-slate-500
                   w-32 sm:w-28 focus:outline-none focus:border-cyan-500"
      />
      {value && (
        <button onClick={() => onChange('')}
                className="text-slate-500 hover:text-slate-200 px-1"
                aria-label="clear DX filter">×</button>
      )}
    </div>
  );
}
